<template>
    <div class="card light-back max-content-width w-100">
        <div class="card-body">
            <div class="small-box">
                <h2>6. {{ $t("Maksmine") }}</h2>
                <div class="help-block">{{ $t("Vaata ule hinna kujunemine ja vali maksevahend") }}</div>
                <div v-if="captchaError" class="alert alert-danger">
                    {{ $t('captcha_failed') }}
                </div>
                <table class="w-100 price-table">
                    <thead>
                        <tr class="">
                            <th class="p-2">{{ $t('Tuup') }}</th>
                            <th class="p-2">{{ $t('Valjaanne') }}</th>
                            <th class="p-2">{{ $t('Hind') }}</th>
                            <th class="p-2">{{ $t('Kogus') }}</th>
                            <th class="p-2">{{ $t('Summa') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="price in currentPrices" class="price-info">
                            <td class="p-2">{{ $t(price.type) }}</td>
                            <td class="p-2">{{ price.name[locale] }}</td>
                            <td class="p-2">{{ price.singlePrice.toFixed(2) }}</td>
                            <td class="p-2">{{ price.amount.toFixed(0) }}</td>
                            <td class="p-2">{{ (price.singlePrice * price.amount).toFixed(2) }}</td>
                        </tr>
                    </tbody>
                </table>
                <div class="w-100 mt-2" style="overflow: auto">
                    <div class="float-right">
                        <span>{{ $t('Kaibemaks') }}: {{ totalVat.toFixed(2) }} €</span>
                    </div>
                </div>
                <div style="overflow: auto" class="final-price mt-1">
                    <span class="float-right">Kokku: <span
                        class="add-header font-weight-bold">{{ totalPrice.toFixed(2) }} €</span></span>
                </div>
                <div class="col" v-if="store.termsText">
                    <div class="row p-1 needs-validation">
                        <div class="custom-control custom-checkbox mb-3">
                            <input type="checkbox" class="custom-control-input" v-model="agreeTerms" v-bind:class="{'is-invalid': termsRequired}" id="customControlValidation1" required>
                            <label class="custom-control-label" for="customControlValidation1">
                                <a href="https://www.ohtulehtkirjastus.ee/uldtingimused/" target="_blank" class="terms">{{ $t('Noustun tingimustega') }}</a>
                            </label>
                            <div class="invalid-feedback">{{ $t('Tingimusetega peab noustuma') }}</div>
                        </div>
                    </div>
                </div>

                <div class="col-12 realbold">
                    <div class="row p-1">
                        {{ $t('Vali maksevahend') }}
                    </div>
                </div>
                <div class="col">
                    <div class="row">
                        <div v-for="method in paymentMethods" class="col-sm-6 mb-3 payment-method">
                            <a @click="onSubmit(method.source)" class="payment-link">
                                <img class="w-100" :src="method.logo_url">
                            </a>
                        </div>
                        <div class="col-sm-6 mb-3 payment-method" :style="{ display: hasGoogleError ? 'none' : 'flex'}">
                            <div id="google-pay-container" :style="{ opacity: hasContactedEveryPay && googlePayClient ? 1 : 0, pointerEvents: hasContactedEveryPay && !paymentInitiated ? 'auto' : 'none'  }"></div>
                        </div>
                        <div 
                            v-if="canMakeApplePayments"
                            class="col-sm-6 mb-3 payment-method d-flex"
                        >
                            <div id="apple-pay-container" :style="{ opacity: hasContactedEveryPay ? 1 : 0, pointerEvents: hasContactedEveryPay && !paymentInitiated ? 'auto' : 'none'  }">
                                <apple-pay-button
                                    @click="onSubmit('applepay')"
                                    type="plain"
                                    class="apple-pay-button apple-pay-button-black"
                                ></apple-pay-button>
                            </div>
                        </div>
                        <div v-if="methodsError" class="methods-error">
                            {{ $t("Maksmine voimatu") }}
                        </div>
                    </div>
                </div>
                <div v-if="isCompany" class="payment-method invoice-payment p-2 m-2 mb-3 justify-content-center">
                    <a @click="onSubmit('invoice')">
                        <i class="fas fa-file"></i>&nbsp;&nbsp;&nbsp;{{ $t('Maksa arve alusel') }}
                    </a>
                </div>

                <div v-if="store.privacyText" class="col">
                    <div class="row">
                        <div class="p-1">
                            <a href="https://www.ohtulehtkirjastus.ee/andmekaitsetingimused/" target="_blank" class="terms">Andmekaitsetingimused</a>
                        </div>
                    </div>
                </div>
            </div>
            <vue-recaptcha
                v-if="siteKey"
                ref="recaptcha"
                @verify="onVerify"
                @expired="onExpired"
                size="invisible"
                :sitekey="siteKey">
            </vue-recaptcha>
        </div>
    </div>
</template>

<script setup>
import { VueRecaptcha } from 'vue-recaptcha';
import { useMainStore } from '../includes/store';
import { useSystem } from '../composables/useSystem.js';
import { useTax } from '../composables/useTax.js';
import { ref, onMounted, onBeforeUpdate, watch, nextTick, computed, onActivated } from 'vue';
import { useI18n } from 'vue-i18n';

const store = useMainStore();

const { isDev } = useSystem();
const { getVATAmount } = useTax();
const { locale } = useI18n();

const agreeTerms = ref(null);
const paymentInitiated = ref(false);
const currentPrices = ref([]);
const paymentMethods = ref([]);
const captchaToken = ref(null);
const type = ref(null);
const captchaError = ref(false);
const methodsError = ref(false);
const hasContactedEveryPay = ref(false);
const googlePayClient = ref(null);
const hasGoogleError = ref(false);
const recaptcha = ref(null);

onMounted(async () => {
    await getPriceInfo();
    getPaymentMethods();
    loadState();
    checkCaptchaError();
    checkEmpty();
})

onActivated(async () => {
    await getPriceInfo();
    getPaymentMethods();
    checkEmpty();
})

onBeforeUpdate(() => {
    checkEmpty();
})

const checkCaptchaError = () => {
    axios.get("/session-info").then(response => {
        if (response.data.captcha === "invalid") {
            paymentInitiated.value = false;
            console.log("Catcha check failed: ", response);
            captchaError.value = true;
        }
    });
}

const initiateGooglePay = () => {
    const googlePayClientCurrent = GooglePayClient?.initialize({ testEnv: isDev() });

    if (googlePayClientCurrent) {
        const googlePayClicked = () => {
            console.log('GooglePay clicked');
            onSubmit('googlepay');
        };

        googlePayClientCurrent.addGooglePayButton({
            buttonColor: 'default',
            buttonType: 'plain',
            buttonSizeMode: 'fill',
            onClick: googlePayClicked,
        })

        googlePayClient.value = googlePayClientCurrent;
    } else {
        hasGoogleError.value = true;
    }
}
const handleGooglePayResponse = async (response) => {
    const { data } = await axios.get(window.location.origin + '/banklink/start/' + type.value + "?g-recaptcha-response=" + response + "&locale=" + locale.value) ?? {};
    if (typeof data === 'string' && data.trim().startsWith('<!DOCTYPE html')) {
        console.warn('%cReceived HTML instead of JSON — redirecting...', 'color: red; font-weight: bold;');
        window.location.href = '/';
        return;
        }
    const responseParams = {
        access_token: data.mobile_access_token,
        api_username: data.api_username,
        account_name: data.account_name,
        payment_reference: data.payment_reference,
        country_code: data.descriptor_country,
        currency_code: data.currency,
        payment_link: data.payment_link,
        amount: `${data.initial_amount}`,
    }
    const googlePayClientInstance = googlePayClient.value;
    googlePayClientInstance.configure(responseParams);

    googlePayClientInstance.createGooglePayPayment();
    paymentInitiated.value = false;
}
 const onSubmit = (paymentType) => {
    if (agreeTerms.value !== true) {
        agreeTerms.value = false;
        return;
    }
    if (paymentInitiated.value) {
        return;
    }
    paymentInitiated.value = true;
    type.value = paymentType;

    if (!recaptcha.value) {
        console.log("Recaptcha not loaded");
        return;
    }
    // DEV restore for production
    recaptcha.value.execute();
    // DEV remove for production
    // this.onVerify();
}

const onVerify = (response) => {
    console.log('Verify: ' + response);
    if (type.value === 'googlepay') {
        handleGooglePayResponse(response);
    } else {
        window.location.href = '/banklink/start/' + type.value + "?g-recaptcha-response=" + response + "&locale=" + locale.value;
    }
}

const onExpired = () => {
    console.log('Recaptcha Expired');
    location.reload();
}

const getPriceInfo = async () => {
    try {
        const response = await axios.get('/payment/info');
        console.log("Price info from server", response.data);
        currentPrices.value = response.data;
    } catch (error) {
        console.log("Payment info is missing, checking more data of dates");
        store.changeStep('dates');
    }
}

const getPaymentMethods = () => {
    axios.get('/payment/methods').then(response => {
        console.log("Payment methods from EveryPay", response.data);
        paymentMethods.value = response.data;
        if (!response.data) {
            methodsError.value = true;
        } else {
            console.log("Payment methods loaded successfully");
            methodsError.value = false;
            hasContactedEveryPay.value = true;
        }
    }).catch(_ => {
        console.log("Failed to load payment methods");
        methodsError.value = true;
    })
}

const loadState = () => {
    checkEmpty();
}

const checkEmpty = () => {
    if (!store.stateLoaded || store.step !== 'payment') {
        return;
    }
    let empty = true;

    if (store.step === "payment") {
        Object.entries(store.stepInfo.dates.selectedDates).forEach(([key, paperDate]) => {
            if (paperDate.length > 0) {
                empty = false;
            }
        });
    }

    if (empty || totalPrice.value === 0) {
        store.changeStep('dates');
    }
}
    
const termsRequired = computed(() => {
    return agreeTerms.value === false;
})

const isCompany = computed(() => {
    return store.stepInfo.client && store.stepInfo.client.type === 'company';
})
const totalPrice = computed(() => {
    return netPrice.value + totalVat.value;
})

const netPrice = computed(() => {
    let total = 0;
    currentPrices.value.forEach(price => {
        total += price.amount * price.singlePrice;
    }); 
    return total;
})

const totalVat = computed(() => {
    return getVATAmount(netPrice.value);
})

const siteKey = computed(() => {
    return process.env.MIX_GOOGLE_RECAPTCHA_KEY
})

watch(() => hasContactedEveryPay.value, (newVal) => {
    if (newVal) {
        nextTick(() => {
            initiateGooglePay();
        });
    }
});

const canMakeApplePayments = computed(() => {
    return window.ApplePaySession && window.ApplePaySession.canMakePayments();
})

</script>

<style scoped>

apple-pay-button {
  --apple-pay-button-width: 100%;
  --apple-pay-button-height: 46px;
  --apple-pay-button-border-radius: 4px;
}

#apple-pay-container {
    width: calc(100% - 10px);
    margin-top: 5px;
    height: 46px;
}

.is-invalid {
    border: none;
    -webkit-box-shadow: 0px 0px 0px 1px rgba(255, 0, 0, 1);
    -moz-box-shadow: 0px 0px 0px 1px rgba(255, 0, 0, 1);
    box-shadow: 0px 0px 0px 1px rgba(255, 0, 0, 1);
}

.terms {
    color: #222222;
}

.fas {
    color: black;
}

.payment-method {
    display: flex;
    cursor: pointer;
    justify-content: center;
    align-items: center;
}

.payment-link {
    box-shadow: 0 0 0.4em gray;
    margin: 5px;
}

.payment-link img {
    max-height: 46px;
    margin: 0 auto;
}

@media (max-width: 576px) {
    .payment-link {
        width: 100%;
    }
}

#google-pay-container {
    width: calc(100% - 10px);
    margin-top: 5px;
    height: 46px;
}

.methods-error {
    color: rgba(255, 0, 0, 1);
    font-weight: 500;
}

.invoice-payment {
    border-width: 1px;
    border-style: solid;
    margin-left: 20px !important;
    margin-right: 20px !important;
}

.final-price {
    font-size: 28px;
}

.coupon {
    text-transform: uppercase;
    font-weight: 500;
}

th {
    font-weight: 500;
}

.price-table > tr:first-child, .price-table > tr:last-child {
    border-bottom: solid;
    border-width: 1px 0;
    border-color: #B5B5B5;
}

</style>
